import React, { Dispatch, JSX, SetStateAction, useEffect, useRef } from "react";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./Popover.module.css";
import PopoverContent, {
  PopoverContentPosition,
  PopoverContentProps,
  Position,
} from "./PopoverContent";
import PopoverTrigger, { PopoverTriggerProps } from "./PopoverTrigger";

type PopoverProps = {
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  openAfterClick?: boolean;
  cancelToggleAfterClick?: boolean;
  closeWhenClickOutside?: boolean;
  children: [React.ReactElement<PopoverContentProps>, React.ReactElement<PopoverTriggerProps>];
  className?: string;
};

export default function Popover({
  isVisible = false,
  setIsVisible,
  openAfterClick = false,
  cancelToggleAfterClick = false,
  closeWhenClickOutside = false,
  children,
  className = "",
}: PopoverProps): JSX.Element {
  const popoverRef = useRef(null);
  const popoverContentRef = useRef(null);

  let contentPosition: undefined | PopoverContentPosition;

  const showPopover = () => {
    if (!openAfterClick) {
      setIsVisible(true);
    }
  };

  const hidePopover = () => {
    if (!openAfterClick) {
      setIsVisible(false);
    }
  };

  const togglePopover = (e: React.MouseEvent<HTMLElement>) => {
    if (isVisible && cancelToggleAfterClick) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (openAfterClick) {
      setIsVisible(!isVisible);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        closeWhenClickOutside &&
        popoverRef.current &&
        e.target &&
        !(popoverRef.current as Node).contains(e.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (closeWhenClickOutside) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    const handleContentPosition = () => {
      if (popoverContentRef.current && contentPosition?.top) {
        const contentElement = popoverContentRef.current as HTMLElement;

        if (contentElement && contentPosition?.default) {
          setContentPosition(contentElement, contentPosition.default);
        }

        const triggerRect = contentElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (contentElement) {
          //console.log(contentPosition?.top, triggerRect.top + contentElement.offsetHeight,windowHeight, (windowHeight - contentElement.clientHeight))

          if (
            contentPosition?.top &&
            triggerRect.top + contentElement.offsetHeight >
              windowHeight - contentElement.offsetHeight
          ) {
            setContentPosition(contentElement, contentPosition.top);
          }

          // TODO
        }
      }
    };

    function setContentPosition(element: HTMLElement, position: Position) {
      element.style.top = position.top || "";
      element.style.bottom = position.bottom || "";
      element.style.left = position.left || "";
      element.style.right = position.right || "";
    }

    handleContentPosition();

    globalThis.addEventListener("scroll", handleContentPosition);
    globalThis.addEventListener("resize", handleContentPosition);

    return () => {
      globalThis.removeEventListener("click", handleClickOutside);
      globalThis.addEventListener("scroll", handleContentPosition);
      globalThis.addEventListener("resize", handleContentPosition);
    };
  }, [contentPosition?.top, setIsVisible, contentPosition?.default, closeWhenClickOutside]);

  return (
    <div
      ref={popoverRef}
      className={UStyles.classNames(styles.popover, className)}
      onMouseLeave={hidePopover}
    >
      {React.Children.map(children, (child, index) => {
        if (index === 0) {
          return (
            <PopoverTrigger>
              <div
                className={child.props.className || ""}
                onClick={togglePopover}
                onMouseEnter={showPopover}
                /*
									style={ {
										width: popoverContentRef.current ? popoverContentRef?.current.getBoundingClientRect().width : "auto",
									} }
									 */
              >
                {child.props.children}
              </div>
            </PopoverTrigger>
          );
        }

        contentPosition = (child.props as PopoverContentProps)?.position;

        return (
          <PopoverContent>
            <div
              className={UStyles.classNames(
                styles.popoverContent,
                isVisible ? styles.popoverContentOpen : "",
                isVisible && child.props.className ? child.props.className : "",
              )}
              ref={popoverContentRef}
              style={contentPosition?.default ? contentPosition.default : {}}
            >
              {child.props.children}
            </div>
          </PopoverContent>
        );
      })}
    </div>
  );
}
